import { useInfiniteQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';

import { LoadingPage } from '@nesso/ui/blocks/loading-page';
import { Alert, AlertDescription, AlertTitle } from '@nesso/ui/primitives/alert';

import { practiceMemberUsageInfiniteOptions } from '@/entities/reporting';
import { PageTitle } from '@/shared/ui/text/page-title';
import { AuthenticatedShell } from '@/widgets/layout/authenticated-shell';
import { UserDropdown } from '@/widgets/user/user-dropdown';

import { useReportingSort } from '../model/use-reporting-sort';
import { ReportingPageFreshnessBar } from './reporting-page/reporting-page-freshness-bar';
import { ReportingPagePracticeMemberUsageTable } from './reporting-page/reporting-page-practice-member-usage-table';
import { ReportingPageSearch } from './reporting-page/reporting-page-search';

// Search params instead of a route import: `pages/` may not import `routes/` — that direction is
// reversed, and the route module itself imports this page.
const route = getRouteApi('/reporting');

export function ReportingPage() {
    const { search } = route.useSearch();
    const { sortBy, sortDirection, toggleSort } = useReportingSort();
    const usage = useInfiniteQuery(
        practiceMemberUsageInfiniteOptions({ search, sortBy, sortDirection }),
    );

    const practiceMembers = usage.data?.pages.flatMap((page) => page.practiceMembers) ?? [];
    // Present on the first page only (no cursor sent) — every later page's own summary is null.
    const summary = usage.data?.pages[0]?.summary ?? undefined;

    return (
        <AuthenticatedShell dropdown={<UserDropdown />}>
            {/* pb-1.5 matches the shadowed container's max paint extent (3px offset + 3px blur)
                so `overflow-y-auto` here doesn't clip it. */}
            <main className="flex min-w-0 flex-1 flex-col overflow-y-auto pt-8 pr-20 pb-1.5 pl-10">
                {usage.isPending ? (
                    // h-full overrides LoadingPage's default h-dvh, which would overflow
                    // AuthenticatedShell's own h-dvh chrome and push the sidebar.
                    <LoadingPage className="h-full w-full" label="Loading reporting data…" />
                ) : (
                    // 997px is a floor on the table (`min-w-[997px]` there), not a ceiling here:
                    // past 1440px this row grows instead of capping.
                    <div className="flex min-h-0 w-full flex-1 flex-col gap-8">
                        <div className="flex items-center justify-between">
                            <PageTitle>Reporting</PageTitle>

                            <ReportingPageFreshnessBar
                                dataUpdatedAt={usage.dataUpdatedAt || undefined}
                                // Excludes isFetchingNextPage — paging in more rows is not a
                                // "refresh" and must not disable/spin the manual refresh control.
                                isRefreshing={usage.isFetching && !usage.isFetchingNextPage}
                                onRefresh={() => void usage.refetch()}
                            />
                        </div>
                        <div className="flex min-h-0 flex-1 flex-col">
                            {!usage.data ? (
                                <Alert variant="destructive">
                                    <AlertTitle>Couldn't load reporting data</AlertTitle>
                                    <AlertDescription>
                                        Something went wrong fetching practice member usage. Try
                                        refreshing the page.
                                    </AlertDescription>
                                </Alert>
                            ) : (
                                <>
                                    {/* A failed fetchNextPage/refetch leaves prior pages in `data`, so the
                                        error shows beside the table rather than replacing it. */}
                                    {usage.isError && (
                                        <Alert variant="destructive">
                                            <AlertTitle>
                                                Couldn't load more reporting data
                                            </AlertTitle>
                                            <AlertDescription>
                                                Something went wrong fetching practice member usage.
                                                The rows already loaded are still shown below.
                                            </AlertDescription>
                                        </Alert>
                                    )}
                                    {/* box-shadow, not the `drop-shadow` filter: the frame is a plain
                                        rectangle, so tracing a silhouette buys nothing. */}
                                    {/* No `flex-1`: this block hugs its content so the table below is the
                                        only part that scrolls internally. */}
                                    <div className="flex min-h-0 flex-col shadow-[0_3px_3px_#0000001f]">
                                        <ReportingPageSearch />
                                        <ReportingPagePracticeMemberUsageTable
                                            search={search}
                                            summary={summary}
                                            practiceMembers={practiceMembers}
                                            sortBy={sortBy}
                                            sortDirection={sortDirection}
                                            onSortChange={toggleSort}
                                            hasNextPage={usage.hasNextPage}
                                            isFetchingNextPage={usage.isFetchingNextPage}
                                            onLoadMore={() => void usage.fetchNextPage()}
                                            error={usage.error}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </main>
        </AuthenticatedShell>
    );
}
